import { useCallback, useEffect, useState } from "react";
import { useQuerySnapshotByProduct } from "domains/allocations/services/use-allocation-api";
import { DataGrid, GridCallbackDetails, GridCellParams, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import localisations from "domains/products/localisation";
import { queryPartitionsByIds } from "domains/partitions/services/partition-service";
import { CircularProgress } from "@mui/material";
import { QuerySnapshotByProductOptions } from "domains/allocations/services/allocation-service";

type ProductTableProps = {
    fieldCompanyId: number
    productId: number,
    productName: string,
    onRowClick?: (partitionId: guid) => void | null;
}
function ProductTable(props: ProductTableProps) {
    const [rows, setRows] = useState<GridRowsProp>([]);
    const [showTable, setShowTable] = useState(false);
    const [showSpinner, setShowSpinner] = useState(true);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState<number | null>(0);
    const { productId } = props;
    const [options, setOptions] = useState<QuerySnapshotByProductOptions>({
        productId: productId,
        itemsPerPage: 10,
        PageToken: "1"
    });

    const { loading, data } = useQuerySnapshotByProduct(options);

    const handlePageChange = (pageNumber: number, _: GridCallbackDetails) => {
        if (page !== pageNumber) {
            setShowSpinner(false);
            setPage(pageNumber);
        }
    };

    const handlePageSizeChange = (pageSizeNumber: number, _: GridCallbackDetails) => {
        if (pageSize !== pageSizeNumber) {
            setShowSpinner(false);
            setPage(0);
            setPageSize(pageSizeNumber);
        }
    };

    const setRowsAndShowTable = useCallback((rows: GridRowsProp) => {
        setRowCount(data?.totalCount ?? 0);
        setRows(rows);
        setShowTable(true);
    }, [data?.totalCount]);

    const getColumns = () => {
        let {
            columns: { product, partitionName, partitionReference, quantity },
        } = localisations.partitionsByProductTable;

        let toReturn = [
            {
                field: "productName",
                headerName: product.headerName,
                description: product.description,
                sortable: false,
                filterable: false,
                flex: 0.5,
                renderCell: (params: GridCellParams) => <span>{props.productName}</span>,
            },
            {
                field: "partitionName",
                headerName: partitionName.headerName,
                description: partitionName.description,
                sortable: false,
                filterable: false,
                flex: 0.5,
            },
            {
                field: "partitionReference",
                headerName: partitionReference.headerName,
                description: partitionReference.description,
                sortable: false,
                filterable: false,
                flex: 0.5,
            },
            {
                field: "quantity",
                headerName: quantity.headerName,
                description: quantity.description,
                sortable: false,
                filterable: false,
                flex: 0.5,
            },
            {
                field: "partitionId",
                sortable: false,
                filterable: false,
                flex: 0.5,
                hide: true
            }
        ] as GridColDef[];

        return toReturn;
    }

    

    useEffect(() => {
        setOptions({
            productId,
            itemsPerPage: pageSize,
            PageToken: (page + 1).toString(),
        });
    }, [productId, pageSize, page]);


    useEffect(() => {
        if (data && data.items && !loading) {
            if (data.items.length > 0) {
                queryPartitionsByIds({
                    fieldCompanyId: props.fieldCompanyId,
                    ids: data?.items.map(d => d.partitionId) ?? []
                }).then((response) => {
                    var rows: any = [];
                    data.items.forEach(item => {
                        var partition = response.find(p => p.id === item.partitionId);
                        if (partition !== undefined) {
                            rows.push({ "id": rows.length + 1, "productName": item.productId, "partitionId": partition?.id, "partitionName": partition?.name, "partitionReference": partition?.reference, "quantity": item.quantity });
                        }
                    });
                    setRowsAndShowTable(rows as GridRowsProp);
                })
            }
            else {
                setRowsAndShowTable([]);
            }
        }
    }, [data, loading, props.fieldCompanyId, setRowsAndShowTable]);

    return (
        <div style={{ width: "100%", marginTop: 30 }}>
            {loading && showSpinner && (
                <CircularProgress
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '55%',
                        transform: 'translate(-50%, -55%)',
                    }}
                />
            )}
            {(showTable &&
                <DataGrid
                    autoHeight={true}
                    rows={rows}
                    columns={getColumns()}
                    loading={loading}
                    page={page}
                    pageSize={pageSize}
                    rowsPerPageOptions={[5, 10, 20]}
                    onRowClick={(data: any) => {
                        if (props.onRowClick) {
                            props.onRowClick(data.row.partitionId.toString());
                        }
                    }}
                    rowCount={rowCount == null ? undefined : rowCount}
                    paginationMode="server"
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    disableColumnFilter
                />
            )}
        </div>
    );
}
export default ProductTable;